<script lang="ts">
	import type { RouteDataLoading, RouteDataSuccess } from './types';
	import { tooltip } from '$lib/actions/tooltip/tooltip';
	import Badge from '$lib/components/atoms/badge/Badge.svelte';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Price from '$lib/components/atoms/price/Price.svelte';
	import { distanceUnitLabel } from '$lib/components/constants';
	import { searchExtensionTouchpoint } from '$lib/services/searchExtension';
	import { formatPrice } from '$lib/utils/formatPrice';
	import { getDurationHoursAndMinutes } from '$lib/utils/getDurationHoursAndMinutes';
	import * as m from '$paraglide/messages';
	import { languageTag } from '$paraglide/runtime';
	import { createEventDispatcher } from 'svelte';
	import RouteCardBadge from '../RouteBadge.svelte';
	import RouteCardDuration from './components/RouteCardDuration.svelte';
	import RouteCardRecharge from './components/RouteCardRecharge.svelte';
	import RouteCardSkeleton from './components/RouteCardSkeleton.svelte';

	export let routeData: RouteDataLoading | RouteDataSuccess;
	let extraClasses = '';
	export { extraClasses as class };

	$: ({ data, isLoading } = routeData);
	$: ({ hours, minutes } = getDurationHoursAndMinutes(data?.duration));

	$: className
		= !isLoading && data !== undefined && data.isSelected
			? 'bg-neutral-weak shadow-smooth-bottom-1 rd-2'
			: 'bg-neutral-weakest max-md:shadow-smooth-block rd-2';

	const dispatch = createEventDispatcher<{ select: void; seeDetails: { selectedRoute: number } }>();
</script>

<div
	data-testid={data && `route-card-${data.index + 1}`}
	class="route-card items-center text-coloredneutral text-4 mb-2 md:mb-0 items-center p-4 md:p-2 {isLoading
		? ''
		: 'hover:bg-neutral-weak hover:rd-2 '} {className} {extraClasses}"
	class:route-card-selected={data?.isSelected}
>
	{#if isLoading || data === undefined}
		<RouteCardSkeleton />
	{:else}
		<button
			class="route-card-button bg-transparent border-unset p-0 block w-full cursor-pointer border-coloredneutral-weakest"
			on:click={() => dispatch('select')}
		>
			<div class="flex flex-col gap-2 pb-4 md:pb-0 md:pr-2">
				{#if data.types.length > 0}
					<div class="flex flex-wrap gap-2 h-7 overflow-hidden">
						<!-- eslint-disable-next-line svelte/require-each-key -->
						{#each data.types as routeType}
							<RouteCardBadge type={routeType} isSelected={data.isSelected ?? false} />
						{/each}
					</div>
				{/if}

				<div>
					<div
						class="route-card-data gap-1 w-full text-left overflow-hidden md:overflow-initial text-ellipsis"
					>
						<div data-testid="route-card-duration" class="route-card-duration">
							<RouteCardDuration {hours} {minutes} />
						</div>

						<div
							data-testid="route-card-distance"
							class="route-card-distance flex gap-2 items-center self-end text-md md:self-center"
						>
							{`${data.distance.value} ${distanceUnitLabel[data.distance.unit]}`}
							{#if data.restrictionsCount && data.restrictionsCount > 0}
								<div
									use:tooltip={{
										text: data.isSelected
											? m.route__restrictions_tip__selected()
											: m.route__restrictions_tip__unselected(),
										placement: 'top',
									}}
								>
									<Badge
										text={`${data.restrictionsCount}`}
										icon="warning"
										iconPosition="right"
										color={data.isSelected ? 'warningAlt' : 'inverted'}
										size="sm"
									/>
								</div>
							{/if}
						</div>

						<div class="route-card-co2 text-md">
							{#if data.evData?.rechargePoints.length && data.evData?.rechargePoints.length > 0}
								<RouteCardRecharge evData={data.evData} />
							{:else}
								<span data-testid="route-card-co2">{data.carbonDioxideEmission}</span> kg CO<sub>2</sub>
							{/if}
						</div>

						<div class="route-card-costs flex items-end text-ellipsis overflow-hidden">
							<Price
								price={data.totalCost}
								amountClass="text-8 text-bold line-height-8"
								currencyClass="text-md mb-0.5"
							/>
							{#if data.costs.tolls.amount}
								<div
									data-testid="route-card-included-costs-mobile"
									class="md:hidden text-4 clamp-lines-1 block text-ellipsis overflow-hidden pl-0.5"
								>
									{m.route__included_costs({
										tollCost: formatPrice({
											amount: data.costs.tolls.amount,
											currency: data.costs.tolls.currency,
											language: languageTag(),
											digits: 2,
										}),
									})}
								</div>
							{/if}
						</div>

						{#if data.costs.tolls.amount}
							<div
								data-testid="route-card-included-costs"
								class="route-card-included-costs md:self-center md:text-4 hidden md:block md:text-ellipsis md:overflow-hidden md:whitespace-nowrap"
							>
								{m.route__included_costs({
									tollCost: formatPrice({
										amount: data.costs.tolls.amount,
										currency: data.costs.tolls.currency,
										language: languageTag(),
										digits: 2,
									}),
								})}
							</div>
						{/if}
					</div>
				</div>
			</div>
		</button>
		<div>
			<Button
				color={data?.isSelected ? 'dark' : 'light'}
				text={m.route__details()}
				iconPosition="right"
				icon="east"
				class={data?.isSelected ? undefined : 'text-coloredneutral'}
				onclick={() => {
					searchExtensionTouchpoint.activate('itinerary_details');
					dispatch('seeDetails', { selectedRoute: data?.index ?? 0 },
					);
				}}
			/>
		</div>
	{/if}
</div>

<style>
	.route-card {
		width: 260px;
	}
	.route-card-data {
		display: grid;
		grid-template-areas: 'duration distance' 'costs costs' 'co2 co2';
		grid-auto-columns: repeat(2, auto);
	}

	.route-card-data > * {
		height: fit-content;
	}

	.route-card-distance {
		grid-area: distance;
	}

	.route-card-duration {
		grid-area: duration;
	}

	.route-card-included-costs {
		grid-area: included-costs;
	}

	.route-card-co2 {
		grid-area: co2;
	}

	.route-card-costs {
		grid-area: costs;
	}

	@media screen and (min-width: 960px) {
		.route-card-data {
			display: grid;
			grid-template-areas:
				'duration costs'
				'distance included-costs'
				'co2 co2';
			grid-auto-columns: 45% 55%;
			gap: 2px;
		}
		.route-card {
			min-width: 100%;
			height: 132px;
			display: grid;
			grid-template-columns: auto min-content;
		}

		.route-card-detail {
			height: calc(100% - 4px);
		}
	}
</style>
